/**
 * reportKit — tokens, helpers and shared pieces for the printable report
 * documents (Claude Design "Reports" handoff). Used by DeptReportDoc and
 * StaffReportDoc.
 */

import React from 'react';

/* ---- design tokens ---------------------------------------------------- */
export const INK = '#0a0a0a', YELLOW = '#FFD000', GROUP_TINT = '#fff8dd', RED = '#cc0000';
export const TEAL = '#4A7A91', NAVY = '#1e3a4a', ORANGE = '#c2410c', DAY_TINT = '#f4f6f8';
export const GREEN = '#16a34a', GREEN_DARK = '#15803d', AMBER = '#e4620c';
export const T1 = '#1a1a1a', T2 = '#5a5a5a', T3 = '#6a6a6a', T4 = '#8a8a8a', T5 = '#9a9a9a';
export const R1 = '#dcdcdc', R2 = '#ededed', R3 = '#e5e5e5', R4 = '#f2f2f2', TOTAL_ROW = '#f7f7f7';
export const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
export const NUM = { fontVariantNumeric: 'tabular-nums' };

export const PUB = process.env.PUBLIC_URL;
export const BRAND_LOGO = {
  'KIA CARS': `${PUB}/brand-logos/kia.png`, 'SUZUKI CARS': `${PUB}/brand-logos/suzuki.png`,
  'GWM CARS': `${PUB}/brand-logos/gwm.png`, 'GAC CARS': `${PUB}/brand-logos/gac.png`,
  'USED CARS': `${PUB}/brand-logos/used.png`,
};

// Department manager the report is addressed to. Same people as the Zoho
// contact persons on the MELVILLE MOTORS account.
export const DEPT_MANAGER = {
  'KIA CARS': 'Graham McAullay',
  'SUZUKI CARS': 'Rafe Bonini',
  'GWM CARS': 'Rafe Bonini',
  'GAC CARS': 'Rafe Bonini',
  'USED CARS': 'Andrew New',
  SERVICE: 'James Stewart',
  OFFICE: 'Accounts',
};

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAYS_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
export const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;
export const parseISO = (s) => { const [y, m, d] = String(s || '').slice(0, 10).split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1); };
export const dmy = (s) => { const d = parseISO(s); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; };
export const longDate = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
export const dayLabel = (s) => { const d = parseISO(s); return `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`; };
// "1 job" / "2 jobs" — the handoff asks for singularised counts.
export const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

// Monday–Sunday range of an ISO week, e.g. "20 – 26 July 2026".
export function isoWeekRange(week, year) {
  const jan4 = new Date(year, 0, 4);
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (week - 1) * 7);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return mon.getMonth() === sun.getMonth()
    ? `${mon.getDate()} – ${sun.getDate()} ${MONTHS[mon.getMonth()]} ${sun.getFullYear()}`
    : `${mon.getDate()} ${MONTHS[mon.getMonth()]} – ${sun.getDate()} ${MONTHS[sun.getMonth()]} ${sun.getFullYear()}`;
}

// Comments print in Title case; an em dash stands in for empty (never "N/A").
export function titleComment(c) {
  const s = String(c || '').trim();
  if (!s || /^n\/?a$/i.test(s)) return null;
  return s.split(/\s*·\s*|\s*,\s*/).filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' · ');
}

/* ---- shared styles ---------------------------------------------------- */
export const lbl = { fontSize: 8, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: T4 };
export const th = { fontSize: 8.5, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: '#fff', background: INK, padding: '8px 10px', textAlign: 'left' };
export const td = { fontSize: 11, color: T1, padding: '5px 10px', borderBottom: `1px solid ${R2}` };

export function MetaCell({ label, value, sub, first, last }) {
  return (
    <div style={{ flex: 1, paddingLeft: first ? 0 : 12, paddingRight: 12, borderLeft: first ? 'none' : `1px solid ${R2}`, textAlign: last ? 'right' : 'left' }}>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: T1, marginTop: 3, ...NUM }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: T3, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Masthead: ACTS logo · kicker · brand logo or typographic seal + subject ·
// period line · right-hand yellow total block + mono reference.
export function Masthead({ kicker, subject, seal, logo, period, totalLabel, total, reference }) {
  return (
    <div style={{ display: 'flex', gap: 20, paddingBottom: 14, alignItems: 'flex-start' }}>
      <img className="deptdoc-logo" src={`${PUB}/ACTS_CAR_DETAILING.png`} alt="ACTS"
        style={{ width: 84, height: 84, objectFit: 'contain', flex: 'none' }} />
      <div style={{ flex: 1 }}>
        <div style={{ ...lbl, letterSpacing: 2.2 }}>{kicker}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '6px 0 5px' }}>
          {logo
            ? <img className="deptdoc-brand" src={logo} alt={subject} style={{ height: 38, maxWidth: 140, objectFit: 'contain' }} />
            : <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.6, border: `2px solid ${INK}`, padding: '5px 9px', lineHeight: 1 }}>{seal}</span>}
          <span style={{ width: 1, height: 30, background: R1 }} />
          <span style={{ fontSize: 31, fontWeight: 800, letterSpacing: -0.6, color: INK }}>{subject}</span>
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: T2, ...NUM }}>{period}</div>
      </div>
      {total != null && (
        <div style={{ flex: 'none', textAlign: 'right' }}>
          <div style={lbl}>{totalLabel}</div>
          <div className="deptdoc-total" style={{ background: YELLOW, color: INK, fontSize: 25, fontWeight: 800, letterSpacing: -0.5, padding: '7px 15px', marginTop: 4, ...NUM }}>
            {money(total)}
          </div>
          {reference && <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 600, color: T4, marginTop: 5, letterSpacing: 0.5 }}>{reference}</div>}
        </div>
      )}
    </div>
  );
}

// Group header bar inside the table (service = yellow, vehicle = teal,
// day = navy).
export function GroupBar({ accent, tint, children }) {
  return (
    <tr className="deptdoc-group">
      <td colSpan={99} style={{ padding: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, padding: '6px 10px',
          background: tint, borderLeft: `3px solid ${accent}`,
        }}>{children}</div>
      </td>
    </tr>
  );
}

// Compact day-production bar for the hourly staff day groups (80×6 per the
// handoff). Same rules as the live meter: the day's production against the
// full-day wage (tick) and the goal (wage + $50).
export function DayBar({ production, wage }) {
  const w = parseFloat(wage) || 228;
  const goal = w + 50;
  const pct = goal > 0 ? Math.round((production / goal) * 100) : 0;
  const frac = Math.min(goal > 0 ? production / goal : 0, 1);
  const markPct = Math.min(goal > 0 ? w / goal : 0, 1) * 100;
  const hitWage = production >= w;
  const hitGoal = production >= goal;
  const color = hitWage ? GREEN : AMBER;
  const labelColor = hitGoal ? GREEN_DARK : hitWage ? GREEN : ORANGE;
  return (
    <>
      <span style={{ position: 'relative', width: 80, height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden', flex: 'none' }}>
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${frac * 100}%`, background: color, borderRadius: 999 }} />
        <span style={{ position: 'absolute', top: 0, bottom: 0, left: `${markPct}%`, width: 2, background: hitWage ? GREEN_DARK : '#475569' }} />
      </span>
      <span style={{ fontSize: 9.5, fontWeight: 800, color: labelColor, ...NUM }}>{pct}%</span>
    </>
  );
}

// TOTAL SERVICE card used at the foot of every document.
export function TotalServiceCard({ counts }) {
  const sorted = Object.entries(counts).map(([s, n]) => [s, Math.round(n * 100) / 100])
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const total = Math.round(sorted.reduce((s, [, n]) => s + n, 0) * 100) / 100;
  return (
    <div style={{ width: 214, flex: 'none' }}>
      <div className="deptdoc-cardhead" style={{ background: INK, color: '#fff', fontSize: 8.5, fontWeight: 800, letterSpacing: 1.4, padding: '6px 10px', textTransform: 'uppercase' }}>Total Service</div>
      <div style={{ border: `1px solid ${R3}`, borderTop: 'none' }}>
        {sorted.map(([s, n]) => (
          <div key={s} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: `1px solid ${R4}` }}>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: TEAL }}>{s}</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, ...NUM }}>{n}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', background: TOTAL_ROW }}>
          <span style={{ fontSize: 10.5, fontWeight: 800 }}>TOTAL</span>
          <span style={{ fontSize: 10.5, fontWeight: 800, ...NUM }}>{total}</span>
        </div>
      </div>
    </div>
  );
}

// Right-hand totals stack: two plain lines then the yellow grand row.
export function TotalsStack({ lines, grandLabel, grandValue }) {
  return (
    <div style={{ width: 290, marginLeft: 'auto', marginTop: 14 }}>
      {lines.map((l, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${i === 0 ? R1 : R2}`, padding: '5px 12px' }}>
          <span style={{ fontSize: 10.5, fontWeight: 600, color: T2 }}>{l.label}</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: l.green ? GREEN_DARK : T1, ...NUM }}>{money(l.value)}</span>
        </div>
      ))}
      <div className="deptdoc-grand" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: YELLOW, borderTop: `2px solid ${INK}`, padding: '9px 12px' }}>
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, color: INK }}>{grandLabel}</span>
        <span style={{ fontSize: 17, fontWeight: 800, color: RED, ...NUM }}>{money(grandValue)}</span>
      </div>
    </div>
  );
}

// Footer group + bottom page rule, shared by every document.
export function DocFooter({ counts, notes, docName, right }) {
  return (
    <>
      <div className="deptdoc-footer" style={{ borderTop: `1px solid ${R1}`, paddingTop: 16, marginTop: 18, display: 'flex', gap: 22, alignItems: 'flex-end' }}>
        <TotalServiceCard counts={counts} />
        <div style={{ flex: 1 }}>
          <div style={lbl}>Notes</div>
          <div style={{ fontSize: 10, lineHeight: 1.55, color: T2, marginTop: 4 }}>{notes}</div>
          <div style={{ display: 'flex', gap: 22, marginTop: 10 }}>
            <div><div style={lbl}>Contact</div><div style={{ fontSize: 10, fontWeight: 600, marginTop: 2 }}>Michel Carrascoza</div></div>
            <div><div style={lbl}>Email</div><div style={{ fontSize: 10, fontWeight: 600, marginTop: 2 }}>accounts@actscardetailing.com.au</div></div>
          </div>
        </div>
        <img className="deptdoc-thanks" src={`${PUB}/THANK_YOU.jpg`} alt="Thank you"
          style={{ width: 104, flex: 'none', objectFit: 'contain' }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `3px solid ${INK}`, marginTop: 12, paddingTop: 7 }}>
        <span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 1.4, color: T4, textTransform: 'uppercase' }}>
          Acts Car Detailing · {docName}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: 0.5, color: T4 }}>{right}</span>
      </div>
    </>
  );
}
